import { FC, useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import { StatsCard } from '../../components/admin/StatsCard';
import { adminApi } from '../../api/adminApi';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  DollarSign, Calendar, Users, ShoppingCart,
  TrendingUp, TrendingDown, Clock, Award, BarChart3,
} from 'lucide-react';
import { formatCurrencyAmount } from '../../utils/currency';
import type { ExecutiveAnalytics, BookingAnalytics } from '../../types/admin.types';

type PageState = 'loading' | 'loaded' | 'error';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const DATE_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'This Year', days: 365 },
];

function getDateRange(days: number): { startDate: string; endDate: string } {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  return {
    startDate: startDate.toISOString().split('T')[0] as string,
    endDate: endDate.toISOString().split('T')[0] as string,
  };
}

export const AnalyticsDashboardPage: FC = () => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [executive, setExecutive] = useState<ExecutiveAnalytics | null>(null);
  const [booking, setBooking] = useState<BookingAnalytics | null>(null);
  const [selectedRange, setSelectedRange] = useState(30);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAnalytics = useCallback(async () => {
    try {
      setPageState('loading');
      const { startDate, endDate } = getDateRange(selectedRange);

      const [execData, bookingData] = await Promise.all([
        adminApi.getExecutiveAnalytics({ startDate, endDate }),
        adminApi.getBookingAnalytics({ startDate, endDate }),
      ]);

      setExecutive(execData);
      setBooking(bookingData);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load analytics');
      setPageState('error');
    }
  }, [selectedRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (pageState === 'loading') {
    return (
      <AdminLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 w-64 bg-gray-200 dark:bg-slate-700 rounded" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-slate-700 rounded-xl" />
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 dark:bg-slate-700 rounded-xl" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (pageState === 'error') {
    return (
      <AdminLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">!</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">Error Loading Analytics</h2>
            <p className="text-gray-600 dark:text-slate-400 mb-4">{errorMessage}</p>
            <button onClick={fetchAnalytics} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Try Again
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const growth = executive?.growth;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Analytics Dashboard
            </h1>
            <p className="mt-1 text-gray-500 dark:text-slate-400">Business intelligence and insights</p>
          </div>
          <select
            value={selectedRange}
            onChange={(e) => setSelectedRange(Number(e.target.value))}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 px-3 text-sm"
          >
            {DATE_RANGES.map((range) => (
              <option key={range.days} value={range.days}>{range.label}</option>
            ))}
          </select>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Revenue"
            value={formatCurrencyAmount(executive?.currentPeriod.totalRevenue || 0)}
            icon={<DollarSign className="h-6 w-6" />}
            trend={growth ? { value: Math.round(growth.revenueGrowth), isPositive: growth.revenueGrowth >= 0 } : undefined}
          />
          <StatsCard
            title="Total Bookings"
            value={executive?.currentPeriod.totalBookings || 0}
            icon={<Calendar className="h-6 w-6" />}
            trend={growth ? { value: Math.round(growth.bookingsGrowth), isPositive: growth.bookingsGrowth >= 0 } : undefined}
          />
          <StatsCard
            title="New Customers"
            value={executive?.currentPeriod.newCustomers || 0}
            icon={<Users className="h-6 w-6" />}
            trend={growth ? { value: Math.round(growth.customersGrowth), isPositive: growth.customersGrowth >= 0 } : undefined}
          />
          <StatsCard
            title="Avg Order Value"
            value={formatCurrencyAmount(executive?.currentPeriod.averageOrderValue || 0)}
            icon={<ShoppingCart className="h-6 w-6" />}
          />
        </div>

        {/* Charts Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Daily Bookings Trend */}
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4">Daily Bookings Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={booking?.dailyTrend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v: string) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#f9fafb' }} />
                <Line type="monotone" dataKey="count" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Bookings by Day of Week */}
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4">Bookings by Day of Week</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={booking?.byDayOfWeek || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#f9fafb' }} />
                <Bar dataKey="count" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Bookings by Hour */}
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4">Bookings by Hour</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={booking?.byHour || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v: number) => `${v}:00`} />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#f9fafb' }} />
                <Bar dataKey="count" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Status Distribution */}
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4">Booking Status Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={booking?.statusDistribution || []} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {(booking?.statusDistribution || []).map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#f9fafb' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Insights Section */}
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
          <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4">Key Insights</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <InsightCard icon={<Calendar className="h-5 w-5 text-blue-600" />} label="Peak Day" value={booking?.peakDay || executive?.peakDay || 'N/A'} />
            <InsightCard icon={<Clock className="h-5 w-5 text-green-600" />} label="Peak Hour" value={booking?.peakHour !== null && booking?.peakHour !== undefined ? `${booking.peakHour}:00` : 'N/A'} />
            <InsightCard icon={executive?.cancellationRate && executive.cancellationRate > 10 ? <TrendingDown className="h-5 w-5 text-red-600" /> : <TrendingUp className="h-5 w-5 text-green-600" />} label="Cancellation Rate" value={`${(executive?.cancellationRate || 0).toFixed(1)}%`} />
            <InsightCard icon={<Award className="h-5 w-5 text-purple-600" />} label="Top Service" value={executive?.topService?.name || 'N/A'} />
            <InsightCard icon={<Users className="h-5 w-5 text-orange-600" />} label="Top Therapist" value={executive?.topTherapist?.name || 'N/A'} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const InsightCard: FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-700">
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <p className="text-xs text-gray-500 dark:text-slate-400">{label}</p>
      <p className="font-medium text-gray-900 dark:text-slate-100 truncate">{value}</p>
    </div>
  </div>
);

export default AnalyticsDashboardPage;
